import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Studio.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="st-modal-overlay" onClick={onClose}>
      <div className="st-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="st-modal__image" />
        <span className="st-modal__label">{label}</span>
      </div>
    </div>
  );
}

const resizeImage = (file, maxDim = 2048) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim && file.size < 3_500_000) { resolve(file); return; }
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      width = Math.round(width * scale); height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.src = URL.createObjectURL(file);
  });

const MODELS = [
  { id: "sdxl", label: "SDXL (Juggernaut)" },
  { id: "flux2", label: "Flux2.Dev" },
  { id: "nano-banana", label: "Google Nano Banana Pro" },
];

const RATIOS = ["16:9", "3:2", "4:3", "1:1", "9:16"];
const RESOLUTIONS = ["768", "1024", "1280"];

export default function StudioScene() {
  /* --- Scenes list --- */
  const [scenes, setScenes] = useState([]);
  const [selectedScene, setSelectedScene] = useState("");

  /* --- Reference image --- */
  const [refImage, setRefImage] = useState(null);
  const refInputRef = useRef(null);

  /* --- Artifacts reel (from wardrobe/artifacts) --- */
  const [artifacts, setArtifacts] = useState([]);
  const [selectedArtifacts, setSelectedArtifacts] = useState([]);

  /* --- Prompts --- */
  const [idea, setIdea] = useState("");
  const [prompter, setPrompter] = useState("");

  /* --- Settings --- */
  const [model, setModel] = useState("sdxl");
  const [ratio, setRatio] = useState("16:9");
  const [resolution, setResolution] = useState("1024");
  const [seed, setSeed] = useState("");

  /* --- Generation --- */
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);

  /* --- Reel refs --- */
  const scenesReelRef = useRef(null);
  const artifactsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* --- Load scenes on mount --- */
  const loadScenes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/scenes`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setScenes(data.scenes || []);
    } catch {}
  }, []);

  useEffect(() => { loadScenes(); }, [loadScenes]);

  const handleRefUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setRefImage(URL.createObjectURL(resized));
    if (refInputRef.current) refInputRef.current.value = "";
  };

  const toggleArtifact = (url) => setSelectedArtifacts((p) => p.includes(url) ? p.filter((u) => u !== url) : [...p, url]);

  const blobToB64 = async (blobUrl) => {
    const resp = await fetch(blobUrl);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.readAsDataURL(blob);
    });
  };

  const pollRef = useRef(false);
  const pollJob = async (jobId) => {
    pollRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/studio/scene/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const rr = await fetch(`${API_URL}/studio/scene/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const rd = await rr.json();
          if (rd.image) setResult(rd.image);
          setStatus("done");
          pollRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollRef.current = false;
          return;
        }
      } catch (err) { console.error("Poll error:", err); }
    }
    setErrorMsg("Tiempo agotado"); setStatus("error"); pollRef.current = false;
  };

  const canGenerate = (refImage || idea.trim()) && status !== "generating";

  const handleGenerate = async () => {
    setStatus("generating");
    setResult(null);
    setErrorMsg("");
    try {
      const body = {
        idea, prompter, model, ratio, resolution, seed,
        ref_image_b64: refImage ? await blobToB64(refImage) : "",
        artifact_urls: selectedArtifacts,
      };
      const res = await fetch(`${API_URL}/studio/scene/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollJob(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al generar");
      setStatus("error");
    }
  };

  const getResultSrc = () => {
    if (!result) return null;
    return result.startsWith("data:") ? result : `data:image/png;base64,${result}`;
  };

  return (
    <div className="studio-page">
      {/* LEFT */}
      <div className="st-panel st-panel--left">
        <div className="st-header">
          <h2 className="st-panel__title">Crear Escenario</h2>
          <button
            className={`st-btn-primary ${status === "generating" ? "st-btn-primary--loading" : ""}`}
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {status === "generating" ? "Generando..." : "Crear"}
          </button>
        </div>

        {/* Scenes reel */}
        <section className="st-section">
          <span className="st-section__title">Escenarios existentes</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(scenesReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={scenesReelRef}>
              <div className="st-reel-item st-reel-item--add" onClick={() => { setSelectedScene(""); setRefImage(null); setIdea(""); }}>+</div>
              {scenes.map((s) => (
                <div
                  key={s.url || s.name}
                  className={`st-reel-item ${selectedScene === s.url ? "st-reel-item--selected" : ""}`}
                  onClick={() => setSelectedScene(s.url)}
                >
                  <img src={s.url} alt={s.name} />
                  <span className="st-reel-item__name">{s.name?.replace(".png", "").replace(/_/g, " ")}</span>
                </div>
              ))}
              {scenes.length === 0 && <span className="st-reel-empty">Sin escenarios</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(scenesReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Reference image */}
        <section className="st-section">
          <span className="st-section__title">Imagen de referencia del escenario</span>
          <div
            className="st-upload-zone"
            style={refImage ? { minHeight: 140 } : {}}
            onClick={() => !refImage && refInputRef.current?.click()}
          >
            {refImage ? (
              <>
                <img src={refImage} alt="Scene ref" />
                <button className="st-upload-zone__remove" onClick={(e) => { e.stopPropagation(); setRefImage(null); }}>&times;</button>
              </>
            ) : (
              <span>Haz clic para cargar imagen de referencia</span>
            )}
          </div>
          <input ref={refInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleRefUpload} />
        </section>

        {/* Artifacts reel */}
        <section className="st-section">
          <span className="st-section__title">Artefactos (opcional)</span>
          <div className="st-reel st-reel--tall">
            <button className="st-reel__btn" onClick={() => scrollReel(artifactsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={artifactsReelRef}>
              {artifacts.length > 0 ? (
                artifacts.map((a) => (
                  <div
                    key={a.url}
                    className={`st-reel-item st-reel-item--wide ${selectedArtifacts.includes(a.url) ? "st-reel-item--selected" : ""}`}
                    onClick={() => toggleArtifact(a.url)}
                  >
                    <img src={a.url} alt={a.name} />
                    <span className="st-reel-item__name">{a.name?.replace(".png", "").replace(/_/g, " ")}</span>
                  </div>
                ))
              ) : (
                <span className="st-reel-empty">Crea artefactos en Crear Detalles</span>
              )}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(artifactsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Prompts */}
        <section className="st-section">
          <span className="st-section__title">Idea</span>
          <textarea
            className="st-textarea"
            placeholder="Describe el escenario... (ej: habitacion oscura con ventana, luz de luna, muebles antiguos)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
        </section>
        <section className="st-section">
          <span className="st-section__title">Prompter</span>
          <textarea className="st-textarea" placeholder="Prompt tecnico..." value={prompter} onChange={(e) => setPrompter(e.target.value)} />
        </section>

        {/* Bottom bar */}
        <div className="st-bottom-bar">
          <div className="st-select-group">
            <span className="st-select-group__label">Motor</span>
            <select className="st-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Ratio</span>
            <select className="st-select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Resolucion</span>
            <select className="st-select" value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}px</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Semilla</span>
            <input className="st-seed-input" placeholder="random" value={seed} onChange={(e) => setSeed(e.target.value)} />
          </div>
          <span className="st-credits">Creditos est.: --</span>
        </div>

        {errorMsg && <div className="st-error">{errorMsg}</div>}
      </div>

      {/* RIGHT */}
      <div className="st-panel st-panel--right">
        <h2 className="st-panel__title">Resultado</h2>
        <div className="st-result">
          <div className={`st-result__image ${result ? "st-result__image--loaded" : ""}`}>
            {result ? (
              <img src={getResultSrc()} alt="Escenario" />
            ) : (
              <div className="st-results-empty">
                {status === "generating" ? <span className="st-spinner" /> : <p>Describe o carga referencia y pulsa Crear</p>}
              </div>
            )}
          </div>
          {result && (
            <div className="st-result__footer">
              <span className="st-result__label">Escenario</span>
              <div className="st-result__actions">
                <button className="st-btn-secondary">Guardar</button>
                <button className="st-btn-download">&#x2B73;</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Escenario" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
